// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, makeStyles, Card, Checkbox,
  CardHeader, Divider, List, ListItem, ListItemIcon, ListItemText, Grid,
} from '@material-ui/core';

// Internal Dependencies
import { toast } from 'react-toastify';
import MText from '../../../components/Common/MText';
import OverLoading from '../../../components/Loading';
import MButton from '../../../components/MButton';
import Colours from '../../../styles/colours';
import ApiService from '../../../services/apiService';
import { containerHeaderTextStyle } from '../../../styles/common';

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width:           '100%',
    height:          230,
    backgroundColor: theme.palette.background.paper,
    overflow:        'auto',
  },
}));

function getOutletIndex(list, outlet) {
  return list.findIndex((o) => o.id === outlet.id);
}

function not(a, b) {
  return a.filter((item) => getOutletIndex(b, item) === -1);
}

function intersection(a, b) {
  return a.filter((item) => getOutletIndex(b, item) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

const OutletManagementModal = ({ open, onClose, pageId, groundStaffId }) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [checked, setChecked] = useState([]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const getGroundStaffOutlets = async () => {
    try {
      setIsLoading(true);
      const availableOutlets = await ApiService.getOutlets(pageId);
      const { rows: managedOutlets } = await ApiService.getGroundStaffOutlets(pageId, groundStaffId);
      setLeft(not(availableOutlets, managedOutlets));
      setRight(managedOutlets);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (value) => () => {
    const currentIndex = getOutletIndex(checked, value);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const outletList = (title, items) => (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        avatar={(
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
          />
        )}
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
      />
      <Divider />
      <List className={classes.list} dense component="div" role="list">
        {items.map((outlet) => (
          <ListItem key={outlet.id} role="listitem" button onClick={handleToggle(outlet)}>
            <ListItemIcon>
              <Checkbox
                checked={getOutletIndex(checked, outlet) !== -1}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText
              id={`outlet-${outlet.id}`}
              primary={`${outlet.id} - ${outlet.name}`}
              secondary={[outlet.address, outlet.unitNo, outlet.postalCode].filter((item) => item).join(', ')}
            />
          </ListItem>
        ))}
        <ListItem />
      </List>
    </Card>
  );

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const outletIds = right.map((outlet) => outlet.id);
      await ApiService.updateGroundStaffOutlets(pageId, groundStaffId, { outletIds });
      toast.success('Updated outlets managed by ground staff');
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

    useEffect(() => {
      getGroundStaffOutlets();
    }, []);

    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ style: { padding: '1.2rem' } }}>
        {isLoading && <OverLoading />}
        <DialogTitle>
          <MText
            text="Manage Outlets"
            bold
            colour="inherit"
            size={containerHeaderTextStyle.fontSize}
            className="mb-1"
          />
          <MText text={`Ground Staff ID: ${groundStaffId}`} colour="inherit" size="13px" />
        </DialogTitle>
        <DialogContent>
          <div className="w-100 mb-4">
            <Grid
              container
              justifyContent="flex-start"
              alignItems="center"
              className="m-auto"
            >
              <Grid xs={5} className="p-0" item>{outletList('Available Outlets', left)}</Grid>
              <Grid xs={2} item>
                <Grid container direction="column" alignItems="center">
                  <MButton
                    variant="outlined"
                    onClick={handleCheckedRight}
                    disabled={leftChecked.length === 0}
                    text=">"
                  />
                  <div className="mb-2" />
                  <MButton
                    variant="outlined"
                    onClick={handleCheckedLeft}
                    disabled={rightChecked.length === 0}
                    text="<"
                  />
                </Grid>
              </Grid>
              <Grid xs={5} className="p-0" item>{outletList('Ground Staff\'s Outlets', right)}</Grid>
            </Grid>
          </div>
          <div className="d-flex justify-content-end align-items-center mb-2" style={{ gap: 10 }}>
            <MButton variant="outlined" text="Cancel" className="py-2 px-4" onClick={onClose} />
            <MButton
              variant="contained"
              text="Save"
              className="py-2 px-4"
              colour={Colours.Primary}
              textColour={Colours.White}
              textStyle={{ fontSize: '1rem' }}
              onClick={handleSave}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
};

OutletManagementModal.propTypes = {
  open:          PropTypes.bool,
  onClose:       PropTypes.func,
  groundStaffId: PropTypes.number.isRequired,
  pageId:        PropTypes.number.isRequired,
};

OutletManagementModal.defaultProps = {
  open:    false,
  onClose: () => {},
};

export default OutletManagementModal;
